"use client"
import React, { useState, useEffect, useMemo } from "react"
import AssistantService from "../../services/assistant.service";
import { DEFAULT_EXECUTION_MODE, getExecutionModeLabel } from "../../types/assistant.types";
import { 
  ArrowLeft, 
  Save, 
  Upload,
  User,
  Settings,
  Brain,
  Database,
  Loader2,
  Cpu,
  Search,
  Wand2,
  ChevronDown,
  Info
} from "lucide-react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"
import AvatarUploader from "../AvatarUploader/AvatarUploader"
import { resizeImageToDataURL } from "../../utils/imageResize"
import useToast from "../../hooks/useToast"
import ToastContainer from "../ui/ToastContainer"
import { useAuth } from "../../useAuth"
import { useOrganizations } from "../../hooks/useOrganizations"
import SkillStoreSelector, { pruneSkillSettings, pruneSkillIntegrations, getUnboundIntegrationSkills } from "./SkillStoreSelector"
import ModelSelectorDialog from "./ModelSelectorDialog"
import assistantsBot from "../../assets/assistants-bot.png"

const PROVIDER_BRAND_LABELS = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  mistral: "Mistral",
  meta: "Meta",
  cohere: "Cohere",
  xai: "xAI",
  deepseek: "DeepSeek",
  openrouter: "OpenRouter",
};

function getModelDisplayName(name) {
  if (!name) return "-";
  return name;
}

function getProviderBrandLabel(model) {
  if (!model) return "Unknown";
  return model.brand || PROVIDER_BRAND_LABELS[(model.provider || "").toLowerCase()] || (model.provider || "Unknown");
}

export default function AssistantConfigureView({ 
  assistant, 
  onBack, 
  onSave,
  onUpdate,
  llmModels = [],
  templates: contextTemplates = [] // This will be called "avatars" in the UI but kept as "templates" for API compatibility
}) {
  const { toasts, removeToast, success, error } = useToast();
  const { user } = useAuth();
  const { currentAccount } = useOrganizations();
  const activeAccountId = currentAccount?.id || user?.account_id || user?.accountId || user?.sub || null;

  // Visibility filtering is done server-side: listTemplates is called with the
  // active account, and the backend returns public templates plus that
  // account's private ones (ordered). The old client-side filter matched
  // account_id exactly against every template of an unordered, truncated list —
  // it dropped public avatars and silently lost any that fell past the page cap.
  
  // Estados del formulario
  const [saving, setSaving] = useState(false);
  const [avatarBase64, setAvatarBase64] = useState(""); // Imagen en base64
  // Pegar un enlace es el camino secundario: casi todo el mundo sube un
  // archivo. Vivía como un campo permanente con su etiqueta encima, así que
  // ocupaba sitio y peso visual todo el rato para algo que se usa una vez.
  const [avatarUrlOpen, setAvatarUrlOpen] = useState(false);
  const [avatarUrlDraft, setAvatarUrlDraft] = useState("");

  // Local state for templates (avatars)
  const [templates, setTemplates] = useState(contextTemplates || []);
  // Local state for available skills from API
  const [availableSkills, setAvailableSkills] = useState([]);
  // Model selector dialog
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  // Avatar template search and filtering
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedAvatarTags, setSelectedAvatarTags] = useState([]);
  const [showAllAvatarTags, setShowAllAvatarTags] = useState(false);
  // Collapsible section states
  const [skillsExpanded, setSkillsExpanded] = useState(false);
  const [avatarExpanded, setAvatarExpanded] = useState(false);

  // Fetch templates and skills from backend on mount (for always up-to-date dropdown)
  // Templates load server-side and are searched server-side. With no search
  // term we list the account's visible avatars (public + own); with a term we
  // hit /assistant_templates/search so the query runs in the database instead
  // of filtering a truncated page here. Debounced so typing doesn't fire a
  // request per keystroke.
  useEffect(() => {
    if (!activeAccountId) {
      setTemplates([]);
      return;
    }
    const term = templateSearch.trim();
    const handle = setTimeout(async () => {
      try {
        const result = term
          ? await AssistantService.searchTemplates({
              accountId: activeAccountId,
              query: term,
              view: 'summary',
            })
          : await AssistantService.listTemplates({
              view: 'summary',
              accountId: activeAccountId,
            });
        setTemplates(result.success && Array.isArray(result.data) ? result.data : []);
      } catch (e) {
        setTemplates([]);
      }
    }, term ? 300 : 0);
    return () => clearTimeout(handle);
  }, [activeAccountId, templateSearch]);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const result = await AssistantService.getSkills();
        if (result.success && result.data?.skills && Array.isArray(result.data.skills)) {
          setAvailableSkills(result.data.skills);
        } else {
          setAvailableSkills([]);
        }
      } catch (e) {
        setAvailableSkills([]);
      }
    };
    fetchSkills();
  }, []);

  const [formData, setFormData] = useState({
    // Información básica
    name: '',
    description: '',
    avatar: '', // URL para mostrar avatar existente
    type: 'general',
    
    // Configuración del modelo
    modelId: '',
    avatarTemplate: '', // Selected avatar ID
    temperature: 0.7,
    maxTokens: 2048,
    
    // API fields
    memory: '',
    executionMode: DEFAULT_EXECUTION_MODE,
    skills: [], // Skills del assistant
    skillSettings: {}, // Per-skill config (e.g. image_generation allowed_models/default_model)
    skillIntegrations: {}, // Map skill name → integration UUID (per-skill credential binding)

    // Settings avanzados
    memoryEnabled: false,
    acceptsFilesAndImages: false,
    isActive: true,
    isDraft: false,
    
    // Avatar/Template
    templateId: ''
  });

  // Cargar datos del asistente al montar
  useEffect(() => {
    if (assistant) {
      
      const skillsRoot = Array.isArray(assistant.skills) ? assistant.skills : [];
      const skillsConfig = Array.isArray(assistant.config?.skills) ? assistant.config.skills : [];
      const allSkills = skillsRoot.length > 0 ? skillsRoot : skillsConfig;
      // "memory" is system-managed (assigned via memory_enabled toggle) — exclude from selector
      const selectableSkills = allSkills.filter(s => s !== 'memory');
      setFormData({
        name: assistant.name || '',
        description: assistant.description || '',
        avatar: assistant.avatar || '',
        type: assistant.type || 'general',
        modelId: assistant.config?.llmId || '',
        avatarTemplate: assistant.config?.assistantTemplate?.id || assistant.config?.templateId || '',
        temperature: assistant.config?.temperature ?? 0.7,
        maxTokens: assistant.config?.maxTokens ?? 2048,
        memory: assistant.memory || '',
        executionMode: assistant.executionMode || DEFAULT_EXECUTION_MODE,
        skills: selectableSkills,
        skillSettings: assistant.config?.skillSettings || assistant.config?.skill_settings || {},
        skillIntegrations: assistant.config?.skillIntegrations || assistant.config?.skill_integrations || {},
        memoryEnabled: assistant.config?.memoryEnabled ?? false,
        acceptsFilesAndImages: assistant.config?.acceptsFilesAndImages ?? false,
        isActive: assistant.config?.isActive ?? true,
        isDraft: assistant.isDraft ?? false,
        templateId: assistant.config?.assistantTemplate?.id || assistant.config?.templateId || ''
      });
    }
  }, [assistant]);

  const selectedModel = React.useMemo(
    () => (Array.isArray(llmModels) ? llmModels.find((model) => model.id === formData.modelId) : null),
    [llmModels, formData.modelId]
  );

  const selectedModelSupportsVision = React.useMemo(() => {
    const supportFlag =
      selectedModel?.support_vision ??
      selectedModel?.supportVision ??
      selectedModel?.supportsVision;

    if (supportFlag === undefined || supportFlag === null) return true;
    return Boolean(supportFlag);
  }, [selectedModel]);

  useEffect(() => {
    if (!selectedModelSupportsVision && formData.acceptsFilesAndImages) {
      updateFormData('acceptsFilesAndImages', false);
    }
  }, [selectedModelSupportsVision, formData.acceptsFilesAndImages]);

  const safeTemplates = Array.isArray(templates) ? templates : [];

  const orderedModels = useMemo(() => {
    const safe = Array.isArray(llmModels) ? llmModels : [];
    const recommended = safe.find((m) => (m.name || "").toLowerCase().includes("gpt-4o-mini"));
    if (!recommended) return safe;
    return [recommended, ...safe.filter((m) => m.id !== recommended.id)];
  }, [llmModels]);

  const recommendedModel = useMemo(
    () => orderedModels.find((m) => (m.name || "").toLowerCase().includes("gpt-4o-mini")) || orderedModels[0],
    [orderedModels]
  );

  const availableAvatarTags = useMemo(() => {
    const tags = safeTemplates.flatMap((template) =>
      Array.isArray(template.tags) ? template.tags : []
    );
    return Array.from(new Set(tags)).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [safeTemplates]);

  const visibleAvatarTags = useMemo(() => {
    if (showAllAvatarTags) return availableAvatarTags;
    return availableAvatarTags.slice(0, 12);
  }, [availableAvatarTags, showAllAvatarTags]);

  // Name search runs server-side (see the templates effect); here we only refine
  // the loaded set by the selected tags.
  const filteredTemplates = useMemo(() => {
    return safeTemplates.filter((template) => {
      const templateTags = Array.isArray(template.tags) ? template.tags : [];
      return (
        selectedAvatarTags.length === 0 ||
        selectedAvatarTags.every((tag) => templateTags.includes(tag))
      );
    });
  }, [safeTemplates, selectedAvatarTags]);

  const toggleAvatarTag = (tag) => {
    setSelectedAvatarTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]
    );
  };

  const avatarTemplateCards = useMemo(() => {
    const accentClasses = [
      "before:absolute before:-right-6 before:-top-6 before:h-14 before:w-14 before:rounded-full before:bg-fuchsia-500/30 before:blur-xl",
      "before:absolute before:-left-6 before:bottom-0 before:h-14 before:w-14 before:rounded-full before:bg-cyan-500/25 before:blur-xl",
      "before:absolute before:right-2 before:top-1 before:h-10 before:w-10 before:rounded-full before:bg-violet-500/30 before:blur-lg",
      "before:absolute before:left-1 before:top-1 before:h-10 before:w-10 before:rounded-full before:bg-blue-500/25 before:blur-lg",
      "before:absolute before:right-4 before:bottom-1 before:h-10 before:w-10 before:rounded-full before:bg-purple-500/25 before:blur-lg",
    ];
    return filteredTemplates.map((template, index) => ({
      ...template,
      previewSrc: template.avatar || assistantsBot,
      accentClass: accentClasses[index % accentClasses.length],
    }));
  }, [filteredTemplates]);

  const handleSave = async () => {
    // Validaciones
    if (!formData.name.trim()) {
      error('Required field', 'Please enter a name for the agent');
      return;
    }

    // Skills that act through a connected account require the owner to pick
    // which integration to use — saving with an unbound dropdown would create
    // a skill the agent can't actually run.
    const unboundIntegrationSkills = getUnboundIntegrationSkills(
      formData.skills,
      availableSkills,
      formData.skillIntegrations
    );
    if (unboundIntegrationSkills.length > 0) {
      const names = unboundIntegrationSkills
        .map((s) => s.friendly_name || s.name)
        .join(', ');
      error(
        'Integration required',
        `Select an integration for: ${names}`
      );
      return;
    }

    setSaving(true);
    try {
      const assistantData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        type: 'general', // Default type
        skills: formData.skills || [],
        reactAgentId: null,
        memory: formData.memory || "",
        executionMode: formData.executionMode || DEFAULT_EXECUTION_MODE,
        config: {
          llmId: formData.modelId,
          templateId: formData.avatarTemplate, // Usar templateId para el backend
          temperature: formData.temperature,
          maxTokens: formData.maxTokens,
          memoryEnabled: formData.memoryEnabled,
          acceptsFilesAndImages: formData.acceptsFilesAndImages,
          isActive: formData.isActive,
          isDraft: formData.isDraft,
          skills: formData.skills || [],
          skillSettings: pruneSkillSettings(formData.skillSettings, formData.skills),
          skillIntegrations: pruneSkillIntegrations(formData.skillIntegrations, formData.skills)
        },
        projectId: assistant?.projectId || null,
        avatar: avatarBase64 || formData.avatar || null
      };
      // Skills como array real
      const skills = Array.isArray(assistantData.skills) ? assistantData.skills : [];
      // Payload final
      const dataToSend = {
        ...assistantData,
        skills
      };
      if (assistant && assistant.id) {
        if (!assistant.id.trim()) {
          error('Invalid Agent', 'Agent ID is missing or invalid');
          return;
        }
        const updateResult = await onUpdate(assistant.id, dataToSend);
        // onUpdate returns the result from updateAssistant — check success
        if (updateResult && updateResult.success === false) {
          const msg = updateResult.error?.message || 'Could not save changes';
          error('Error', msg);
          return;
        }
      } else {
        if (!user?.user_id && !user?.id) {
          error('Authentication Error', 'User information is missing');
          return;
        }
        if (!currentAccount?.id) {
          error('Account Error', 'Account information is missing');
          return;
        }
        const saveResult = await onSave(dataToSend);
        if (saveResult && saveResult.success === false) {
          const msg = saveResult.error?.message || 'Could not save changes';
          error('Error', msg);
          return;
        }
      }
      success('Saved', assistant?.id ? 'Agent updated successfully' : 'Agent created successfully');
    } catch (err) {
      console.error('Save error:', err);
      error('Error', 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Van aquí, después de `formData` y `updateFormData`, porque los dos los
  // leen. Los puse arriba con el resto del estado y `const` los dejaba en la
  // zona muerta temporal: el componente reventaba al montar con "Cannot access
  // 'formData' before initialization".
  const hasAvatar = Boolean(
    avatarBase64 ||
    (formData.avatar && !String(formData.avatar).includes('00000000-0000-0000-0000-000000000000'))
  );

  const applyAvatarUrl = () => {
    const url = avatarUrlDraft.trim();
    if (!url) return;
    setAvatarBase64("");
    updateFormData('avatar', url);
    setAvatarUrlDraft("");
    setAvatarUrlOpen(false);
  };

  const handleAvatarChange = (base64) => {
    setAvatarBase64(base64);
    updateFormData('avatar', base64);
  };

  return (
    <div className="w-full">
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      {/* Header */}
      <div className="px-2 py-4 md:px-4 lg:px-6 xl:px-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <Button
              onClick={onBack}
              variant="ghost"
              size="sm"
              aria-label="Back to Agents"
              className="flex-shrink-0 text-white hover:text-purple-400 hover:bg-purple-500/10 border border-white/20 hover:border-purple-500/50 transition-all px-2 sm:px-4 py-2 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {/* The label alone is wider than a phone's remaining space. */}
              <span className="hidden font-medium sm:inline">Back to Agents</span>
            </Button>
            <div className="flex min-w-0 items-center space-x-2">
              <Settings className="w-5 h-5 flex-shrink-0 text-purple-400" />
              <div className="min-w-0">
                <span className="text-base font-light text-white sm:text-lg">
                  Configure Assistant
                </span>
                {assistant?.id && (
                  <span className="ml-2 hidden text-xs text-gray-400 sm:inline">
                    (ID: {assistant.id.slice(0, 8)}...)
                  </span>
                )}
                {!assistant?.id && (
                  <span className="ml-2 text-xs text-green-400">
                    (New Assistant)
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Aquí había un "Cancel" que llamaba a `onBack` — exactamente lo
              mismo que la flecha que tiene al lado, y sin ningún aviso de
              cambios sin guardar que lo distinguiera. Era el mismo botón dos
              veces a 40px de distancia, así que se fue.

              Y la que queda sube a la fila del título en vez de ocupar una
              franja propia: a lo ancho era una barra morada de 374px cruzando
              la pantalla antes de que empiece el formulario, con más peso que
              el título al que acompaña. En móvil se abrevia a "Save" para que
              quepa al lado del título. */}
          <div className="flex flex-shrink-0 items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="h-9 bg-primary px-3 text-white hover:bg-violet-700 sm:px-4"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  <span className="sm:hidden">Save</span>
                  <span className="hidden sm:inline">Save Changes</span>
                </>
              )}
            </Button>
        </div>
      </div>

      {/* Contenido del formulario - Layout de 2 Grids */}
      <div className="w-full px-2 py-4 md:px-4 md:py-6 lg:px-6 xl:px-8">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-8">
          
          {/* Grid Izquierdo: Basic Information */}
          <div className="space-y-6">
            
            {/* Sección 1: Basic Information */}
            <div className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
              <div className="mb-4 flex items-center space-x-2 sm:mb-6">
                <User className="w-5 h-5 text-blue-400" />
                <h3 className="text-white font-semibold text-lg">Basic Information</h3>
              </div>
              
              <div className="space-y-4 sm:space-y-6">
                {/* Avatar */}
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-400">Agent Avatar</label>
                  <div className="space-y-4">
                    {/* La imagen se ve entera. Antes llevaba un velo negro a
                        pantalla completa con un botón "Change" en el centro:
                        en escritorio aparecía al pasar el ratón, pero en táctil
                        no hay hover, así que se quedaba fijo tapando el avatar
                        que viene a enseñar.

                        El control es ahora una pastilla en la esquina, que es
                        como lo resuelven las aplicaciones que enseñan una foto
                        de perfil: la imagen manda y el botón se apoya en ella
                        sin cubrirla. */}
                    <div className="flex items-center gap-4">
                      <div className="relative shrink-0">
                        <div className="h-24 w-24 overflow-hidden rounded-2xl border border-white/15 bg-white/5">
                          {hasAvatar ? (
                            <img
                              src={avatarBase64 || formData.avatar}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <User className="h-8 w-8 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => document.getElementById('avatar-upload').click()}
                          aria-label={hasAvatar ? "Change avatar" : "Upload avatar"}
                          className="absolute -bottom-1.5 -right-1.5 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-[#1b1a24] text-gray-200 shadow-lg transition-colors hover:border-violet-400/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70"
                        >
                          <Upload className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-300">
                          {hasAvatar ? "Square images look best." : "PNG or JPG, up to 10 MB."}
                        </p>
                        <button
                          type="button"
                          onClick={() => setAvatarUrlOpen((v) => !v)}
                          className="mt-1 text-xs font-medium text-violet-300 underline-offset-2 transition-colors hover:text-violet-200 hover:underline"
                        >
                          {avatarUrlOpen ? "Cancel" : "Paste a link instead"}
                        </button>
                      </div>
                    </div>

                    {/* El campo iba con `value=""` fijo: se escribía una tecla,
                        `onChange` la guardaba y el campo volvía a vaciarse, así
                        que era imposible pegar un enlace entero. Ahora lleva su
                        propio borrador y se aplica al confirmarlo. */}
                    {avatarUrlOpen && (
                      <div className="flex items-center gap-2">
                        <Input
                          value={avatarUrlDraft}
                          onChange={(e) => setAvatarUrlDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); applyAvatarUrl(); }
                          }}
                          placeholder="https://example.com/avatar.png"
                          className="h-10 min-w-0 flex-1 rounded-lg border-white/10 bg-[#050816]/80 text-sm text-white placeholder:text-gray-500"
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={applyAvatarUrl}
                          disabled={!avatarUrlDraft.trim()}
                          className="shrink-0 bg-primary text-white hover:bg-violet-700 disabled:opacity-40"
                        >
                          Use
                        </Button>
                      </div>
                    )}

                    {/* Hidden AvatarUploader */}
                    <div className="hidden">
                      <AvatarUploader
                        onAvatarChange={handleAvatarChange}
                        onError={(msg) => error('Avatar', msg)}
                      />
                    </div>

                    {/* Hidden file input */}
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (!file.type.startsWith('image/')) {
                          error('Avatar', 'Please select an image file');
                          e.target.value = '';
                          return;
                        }
                        if (file.size > 10 * 1024 * 1024) {
                          error('Avatar', 'File size must be less than 10MB');
                          e.target.value = '';
                          return;
                        }
                        try {
                          const resized = await resizeImageToDataURL(file, {
                            maxDimension: 512,
                            quality: 0.85,
                          });
                          setAvatarBase64(resized);
                          updateFormData('avatar', resized);
                        } catch (err) {
                          error('Avatar', err?.message || 'Failed to process image');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Main Information */}
                <div className="space-y-4">
                  <div>
                    {/* Etiqueta y campo con la misma forma que en el módulo
                        Database, que es la parte de la app donde esto se
                        decidió con más cuidado: rótulo en versalitas y campo
                        sobre `#050816` con borde `white/10`.
                        Antes eran `bg-black/60` y `border-white/20` — más
                        oscuros que la tarjeta que los contiene y con el borde
                        más marcado que el suyo, así que se leían como dos
                        agujeros recortados en la tarjeta. */}
                    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
                      Agent Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                      placeholder="FastShop Order Confirmation (Copy)"
                      className="h-10 w-full rounded-lg border-white/10 bg-[#050816]/80 text-sm text-white placeholder:text-gray-500"
                    />
                  </div>
                  
                  <div>
                    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
                      Description
                    </label>
                    {/* Con el `Textarea` compartido y no un `<textarea>` a
                        pelo: el de aquí traía su propio radio y su propio foco,
                        así que al lado del campo de nombre se veían como piezas
                        de dos sitios distintos.
                        Tres renglones: la descripción de un agente es casi
                        siempre una frase, y el cuarto no se usaba. */}
                    <Textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => updateFormData('description', e.target.value)}
                      placeholder="Order confirmation agent for FastShop"
                      className="min-h-0 w-full resize-none rounded-lg border-white/10 bg-[#050816]/80 text-sm text-white placeholder:text-gray-500"
                    />
                  </div>

                  {/* Skills Section */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setSkillsExpanded((prev) => !prev)}
                      className="flex w-full items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-2">
                        <label className="text-gray-300 text-sm font-medium">Skills</label>
                        {(formData.skills || []).length > 0 && (
                          <span className="rounded-full border border-purple-500/40 bg-purple-500/20 px-2 py-0.5 text-[10px] text-purple-200">
                            {(formData.skills || []).length} selected
                          </span>
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${skillsExpanded ? "rotate-180" : ""}`} />
                    </button>
                    {skillsExpanded && (
                      <div className="mt-2">
                        <SkillStoreSelector
                          skills={availableSkills}
                          selectedSkills={formData.skills || []}
                          onChange={(value) => updateFormData('skills', value)}
                          skillSettings={formData.skillSettings || {}}
                          onSkillSettingsChange={(skillName, settings) =>
                            updateFormData('skillSettings', {
                              ...(formData.skillSettings || {}),
                              [skillName]: settings,
                            })
                          }
                          skillIntegrations={formData.skillIntegrations || {}}
                          onSkillIntegrationChange={(skillName, integrationId) =>
                            updateFormData('skillIntegrations', {
                              ...(formData.skillIntegrations || {}),
                              [skillName]: integrationId,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Sección 3: Advanced Settings */}
            <div className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
              <div className="flex items-center space-x-2 mb-4">
                <Settings className="w-5 h-5 text-orange-400" />
                <h3 className="text-white font-semibold text-lg">Advanced Settings</h3>
              </div>
              
              <div className="space-y-4">
                {/* Memory */}
                <div className="flex items-center justify-between p-4 bg-black/30 rounded-lg border border-white/10">
                  <div>
                    <label className="text-white font-medium text-sm">Memory</label>
                    <p className="text-gray-400 text-xs">Remember conversation context</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.memoryEnabled}
                      onChange={(e) => updateFormData('memoryEnabled', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-purple-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                  </label>
                </div>

                {/* Accept Files & Images */}
                <div className="flex items-center justify-between p-4 bg-black/30 rounded-lg border border-white/10">
                  <div>
                    <label className="text-white font-medium text-sm">Accept Files & Images</label>
                    <p className="text-gray-400 text-xs">Allow users to attach files and images in chat</p>
                  </div>
                  <label
                    className={`relative inline-flex items-center ${
                      selectedModelSupportsVision ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.acceptsFilesAndImages}
                      disabled={!selectedModelSupportsVision}
                      onChange={(e) => updateFormData('acceptsFilesAndImages', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-purple-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                  </label>
                </div>
                {!selectedModelSupportsVision && (
                  <p className="mt-2 text-xs text-amber-200/90">
                    This model does not support vision. The setting is disabled.
                  </p>
                )}

                {/* Draft Mode — comentado temporalmente, no está en uso */}
              </div>
            </div>
          </div>

          {/* Grid Derecho: Información Técnica y Configuración del Modelo */}
          <div className="space-y-6">
            
            {/* Información Técnica (solo lectura) - Movida arriba */}
            {assistant && (
              <div className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
                <div className="flex items-center space-x-2 mb-6">
                  <Info className="w-5 h-5 text-gray-400" />
                  <h3 className="text-white font-semibold text-lg">Technical Information</h3>
                  <span className="text-xs text-gray-500">(Read only)</span>
                </div>
                <div className="space-y-0 divide-y divide-white/5">
                  <div className="flex justify-between items-center py-2.5 text-xs">
                    <span className="text-gray-500">Agent ID</span>
                    <span className="text-gray-300 font-mono">{assistant.id?.substring(0, 8)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 text-xs">
                    <span className="text-gray-500">Project ID</span>
                    <span className="text-gray-300 font-mono">{assistant.projectId?.substring(0, 8) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 text-xs">
                    <span className="text-gray-500">Owner ID</span>
                    <span className="text-gray-300 font-mono">{assistant.ownerId?.substring(0, 8) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 text-xs">
                    <span className="text-gray-500">Created</span>
                    <span className="text-gray-300">{assistant.createdAt ? new Date(assistant.createdAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5 text-xs">
                    <span className="text-gray-500">Reasoning Strategy</span>
                    <span className="text-gray-300">{getExecutionModeLabel(assistant.executionMode)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Sección 2: AI Model Configuration - Movida abajo */}
            <div className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
              <div className="flex items-center space-x-2 mb-6">
                <Brain className="w-5 h-5 text-green-400" />
                <h3 className="text-white font-semibold text-lg">AI Model Configuration</h3>
              </div>
              
              <div className="space-y-6">
                {/* LLM Model Selection */}
                <div>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-purple-300" />
                      <label className="text-gray-300 text-sm font-medium">LLM Model</label>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsModelSelectorOpen(true)}
                      className="h-8 border-white/20 bg-black/40 px-3 text-xs text-white hover:bg-black/60"
                    >
                      View all models
                    </Button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsModelSelectorOpen(true)}
                    className="w-full rounded-xl border border-white/10 bg-black/40 p-3 text-left transition-all duration-200 hover:border-white/30"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">
                          {selectedModel ? getModelDisplayName(selectedModel.name) : "Select a model"}
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          Provider: {selectedModel ? getProviderBrandLabel(selectedModel) : "—"}
                        </p>
                        {!selectedModelSupportsVision && (
                          <p className="mt-1 text-[11px] text-amber-200/90">
                            Vision not supported
                          </p>
                        )}
                      </div>
                      <span className="shrink-0 rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-wide text-gray-300">
                        {recommendedModel?.id === formData.modelId ? "Recommended" : "Selected"}
                      </span>
                    </div>
                  </button>

                  <p className="mt-2 text-xs text-gray-500">{orderedModels.length} models available</p>
                </div>

                {/* Avatar Selection */}
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                  <button
                    type="button"
                    onClick={() => setAvatarExpanded((prev) => !prev)}
                    className="flex w-full items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <Wand2 className="w-4 h-4 text-purple-300" />
                      <label className="text-white">Avatar</label>
                      <span className="text-xs text-gray-500">({safeTemplates.length})</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${avatarExpanded ? "rotate-180" : ""}`} />
                  </button>

                  {avatarExpanded && (
                  <div className="mt-3 space-y-3">

                  <div className="mb-3">
                    <Input
                      value={templateSearch}
                      onChange={(e) => setTemplateSearch(e.target.value)}
                      placeholder="Search avatar by name or description..."
                      className="w-full bg-black/60 border border-white/20 text-white placeholder-gray-500"
                    />
                  </div>

                  {availableAvatarTags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {visibleAvatarTags.map((tag) => {
                        const isActive = selectedAvatarTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => toggleAvatarTag(tag)}
                            className={`rounded-full border px-3 py-1 text-xs transition-all duration-200 ${
                              isActive
                                ? "border-purple-400/70 bg-purple-500/20 text-purple-200"
                                : "border-white/20 bg-black/40 text-gray-300 hover:border-white/35"
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}

                      {availableAvatarTags.length > 12 && (
                        <button
                          type="button"
                          onClick={() => setShowAllAvatarTags((prev) => !prev)}
                          className="rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs text-gray-300 hover:border-white/35"
                        >
                          {showAllAvatarTags
                            ? "Show less"
                            : `+${availableAvatarTags.length - 12} more`}
                        </button>
                      )}

                      {selectedAvatarTags.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedAvatarTags([])}
                          className="rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs text-gray-300 hover:border-white/35"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
                    <button
                      type="button"
                      onClick={() => updateFormData('avatarTemplate', '00000000-0000-0000-0000-000000000000')}
                      className={`h-full min-h-[200px] flex flex-col p-4 text-left rounded-xl border transition-all duration-300 ${
                        formData.avatarTemplate === '00000000-0000-0000-0000-000000000000'
                          ? "bg-purple-600/20 border-purple-500/50 shadow-lg shadow-purple-500/10"
                          : "bg-white/5 border-white/10 hover:border-white/30"
                      }`}
                    >
                      <div className="mb-3 relative overflow-hidden rounded-xl border border-white/20 bg-black/40 p-2 before:absolute before:-right-4 before:top-1 before:h-12 before:w-12 before:rounded-full before:bg-indigo-500/25 before:blur-xl">
                        <div className="flex items-center gap-2">
                          <img src={assistantsBot} alt="No Avatar" className="h-12 w-12 shrink-0 rounded-full border border-white/25 object-cover" />
                          <div className="min-w-0">
                            <p className="truncate text-[11px] text-gray-400">Avatar identity</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start justify-between gap-3 min-h-[56px]">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">No Avatar</p>
                          <p className="mt-1 overflow-hidden text-xs leading-5 text-gray-400 h-[60px]">Start from scratch with your own style.</p>
                        </div>
                        <span className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-1 rounded-full bg-white/10 text-gray-300">
                          {formData.avatarTemplate === '00000000-0000-0000-0000-000000000000' ? "Selected" : "Custom"}
                        </span>
                      </div>
                    </button>

                    {avatarTemplateCards.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => updateFormData('avatarTemplate', template.id)}
                        className={`h-full min-h-[200px] flex flex-col p-4 text-left rounded-xl border transition-all duration-300 ${
                          formData.avatarTemplate === template.id
                            ? "bg-purple-600/20 border-purple-500/50 shadow-lg shadow-purple-500/10"
                            : "bg-white/5 border-white/10 hover:border-white/30"
                        }`}
                      >
                        <div className={`mb-3 relative overflow-hidden rounded-xl border border-white/20 bg-black/40 p-2 ${template.accentClass}`}>
                          <div className="flex items-center gap-2">
                            <img src={template.previewSrc} alt={template.name} className="h-12 w-12 shrink-0 rounded-full border border-white/25 object-cover" />
                            <div className="min-w-0">
                              <p className="truncate text-[11px] text-gray-400">Avatar identity</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-start justify-between gap-3 min-h-[56px]">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-white">{template.name}</p>
                            <p className="mt-1 overflow-hidden text-xs leading-5 text-gray-400 h-[60px]">{template.description || "Avatar preset"}</p>
                          </div>
                          <span className="shrink-0 text-[10px] uppercase tracking-wide px-2 py-1 rounded-full bg-white/10 text-gray-300">
                            {formData.avatarTemplate === template.id ? "Selected" : "Avatar"}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {avatarTemplateCards.length === 0 && safeTemplates.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">No avatars match your search.</p>
                  )}
                  </div>
                  )}
                </div>

                {/* Temperature */}
                <div>
                  <label className="text-gray-300 text-sm font-medium mb-3 block">
                    Temperature: {formData.temperature}
                  </label>
                  <div className="space-y-3">
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={formData.temperature}
                      onChange={(e) => updateFormData('temperature', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Precise (0)</span>
                      <span>Balanced (1)</span>
                      <span>Creative (2)</span>
                    </div>
                  </div>
                </div>
                
                {/* Max Tokens */}
                <div>
                  <label className="text-gray-300 text-sm font-medium mb-3 block">
                    Max Tokens: {formData.maxTokens}
                  </label>
                  <div className="space-y-3">
                    <input
                      type="range"
                      min="256"
                      max="4096"
                      step="256"
                      value={formData.maxTokens}
                      onChange={(e) => updateFormData('maxTokens', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Short (256)</span>
                      <span>Medium (2048)</span>
                      <span>Long (4096)</span>
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>

      <ModelSelectorDialog
        open={isModelSelectorOpen}
        onOpenChange={setIsModelSelectorOpen}
        models={orderedModels}
        selectedModelId={formData.modelId}
        recommendedModelId={recommendedModel?.id}
        onSelectModel={(modelId) => updateFormData('modelId', modelId)}
      />
    </div>
  );
}